/**
 * Słownik polski — źródło prawdy dla zestawu kluczy (§46).
 *
 * Wartość może być tekstem albo zestawem form liczby mnogiej. Polski ma trzy formy
 * (`one` / `few` / `many`) i to nie jest szczegół kosmetyczny: ekran Today mówi wprost
 * „2 sprawy wymagają Twojej uwagi" — z angielskim schematem „1 thing / N things"
 * wyszłoby „5 sprawy wymagają", czyli komunikat wyglądający na zepsuty w aplikacji,
 * której cała obietnica to spokój i zaufanie.
 */
export const pl = {
  // — Marka i nawigacja
  "common.appName": "VeyCue",
  "common.tagline": "Only what needs you.",
  "common.cancel": "Anuluj",
  "common.save": "Zapisz",
  "common.retry": "Spróbuj ponownie",
  "common.loading": "Wczytywanie…",
  "common.days": {
    one: "{count} dzień",
    few: "{count} dni",
    many: "{count} dni",
    other: "{count} dnia",
  },
  "common.daysRemaining": {
    one: "został {count} dzień",
    few: "zostały {count} dni",
    many: "zostało {count} dni",
    other: "zostało {count} dnia",
  },
  "common.daysOverdue": {
    one: "{count} dzień po terminie",
    few: "{count} dni po terminie",
    many: "{count} dni po terminie",
    other: "{count} dnia po terminie",
  },

  "nav.today": "Dziś",
  "nav.cues": "Sygnały",
  "nav.ask": "Zapytaj",
  "nav.documents": "Dokumenty",
  "nav.contracts": "Umowy",
  "nav.timeline": "Historia",
  "nav.connections": "Połączenia",
  "nav.company": "Firma",
  "nav.settings": "Ustawienia",
  "nav.overview": "Przegląd",
  "nav.attentionCenter": "Centrum uwagi",
  "nav.more": "Więcej",

  // — Ekran Today (§21)
  "today.greeting.morning": "Dzień dobry",
  "today.greeting.afternoon": "Dzień dobry",
  "today.greeting.evening": "Dobry wieczór",
  "today.attention": {
    one: "{count} sprawa wymaga Twojej uwagi.",
    few: "{count} sprawy wymagają Twojej uwagi.",
    many: "{count} spraw wymaga Twojej uwagi.",
    other: "{count} sprawy wymaga Twojej uwagi.",
  },
  "today.allClear": "Wszystko inne wygląda dobrze.",
  "today.nothingToDo": "Nic nie wymaga teraz Twojej uwagi.",
  "today.monitoredAreas": {
    one: "{count} monitorowany obszar bez zastrzeżeń",
    few: "{count} monitorowane obszary bez zastrzeżeń",
    many: "{count} monitorowanych obszarów bez zastrzeżeń",
    other: "{count} monitorowanego obszaru bez zastrzeżeń",
  },

  // — Cue
  "cue.severity.critical": "Krytyczne",
  "cue.severity.warning": "Ostrzeżenie",
  "cue.severity.info": "Informacja",
  "cue.status.new": "Nowe",
  "cue.status.viewed": "Przejrzane",
  "cue.status.snoozed": "Odłożone",
  "cue.status.resolved": "Załatwione",
  "cue.status.ignored": "Zignorowane",
  "cue.status.false_positive": "Błędne",
  "cue.action.resolve": "Załatwione",
  "cue.action.snooze": "Odłóż",
  "cue.action.ignore": "Ignoruj",
  "cue.action.notRelevant": "Nie dotyczy mnie",
  "cue.action.wrong": "To błąd",
  "cue.whyItMatters": "Dlaczego to ważne",
  "cue.recommendedAction": "Zalecane działanie",
  "cue.source": "Źródło",
  "cue.viewSource": "Zobacz w dokumencie",
  "cue.action.reviewContract": "Sprawdź umowę",
  "cue.contract_indexation.summary": "Zapis o waloryzacji: {description}",
  "cue.contract_penalty_risk.summary": "Ryzyko: {description}",
  "cue.obligation_due.summary": "Zobowiązanie: {description}",
  "cue.category.contracts": "Umowy",
  "cue.category.payments": "Płatności",
  "cue.category.documents": "Dokumenty",
  "cue.category.deadlines": "Terminy",
  "cue.category.email": "Poczta",
  "cue.category.calendar": "Kalendarz",
  "cue.category.business": "Firma",
  "cue.page": "Strona {page}",
  "cue.confidence": "Pewność analizy: {value}%",
  "cue.empty": "Brak sygnałów w tym widoku.",

  // Tytuły Cue — parametry wstawia silnik, nigdy gotowy tekst
  "cue.contract_auto_renewal.title": "Umowa z {counterparty} przedłuży się automatycznie",
  "cue.contract_notice_deadline.title": "Termin wypowiedzenia umowy — {counterparty}",
  "cue.contract_expiry.title": "Umowa z {counterparty} wygasa",
  "cue.contract_indexation.title": "Waloryzacja w umowie z {counterparty}",
  "cue.contract_penalty_risk.title": "Ryzyko kary umownej — {counterparty}",
  "cue.payment_overdue.title": "Zaległa płatność — {counterparty}",
  "cue.payment_due.title": "Zbliża się płatność — {counterparty}",
  "cue.document_expiry.title": "Dokument traci ważność",
  "cue.insurance_expiry.title": "Polisa kończy się",
  "cue.obligation_due.title": "Zobowiązanie z terminem",
  "cue.response_requested.title": "Oczekiwana odpowiedź — {counterparty}",
  "cue.customer_escalation.title": "Eskalacja od klienta — {counterparty}",
  "cue.calendar_deadline.title": "Termin w kalendarzu",
  "cue.business_deadline.title": "Termin biznesowy",

  // — Company Health (§24)
  "health.title": "Kondycja firmy",
  "health.score": "{score} / 100",
  "health.good": "Dobra",
  "health.needsAttention": "Wymaga uwagi",
  "health.poor": "Słaba",
  "health.why": "Dlaczego taki wynik",
  "health.factor.unresolved_critical": "Nierozwiązane sprawy krytyczne",
  "health.factor.overdue": "Sprawy po terminie",
  "health.factor.due_soon": "Terminy w ciągu 7 dni",
  "health.factor.stale_connections": "Połączenia wymagające ponownej autoryzacji",
  "health.factor.unmonitored_areas": "Zalecane obszary bez monitorowania",

  // — Uwierzytelnianie
  "auth.signIn": "Zaloguj się",
  "auth.signUp": "Załóż konto",
  "auth.signOut": "Wyloguj się",
  "auth.email": "Adres e-mail",
  "auth.password": "Hasło",
  "auth.passwordHint": "Co najmniej {min} znaków. Dłuższa fraza jest bezpieczniejsza niż krótkie hasło ze znakami specjalnymi.",
  "auth.continueWithGoogle": "Kontynuuj z Google",
  "auth.continueWithApple": "Kontynuuj z Apple",
  "auth.startTrial": "Rozpocznij bezpłatny okres próbny",

  // — Onboarding (§9)
  "onboarding.nip.title": "Zacznijmy od NIP-u",
  "onboarding.nip.description": "Pobierzemy publiczne dane firmy, żebyś nie musiał ich przepisywać.",
  "onboarding.nip.label": "NIP",
  "onboarding.nip.invalid": "To nie jest poprawny NIP.",
  "onboarding.confirm": "Czy to Twoja firma?",
  "onboarding.questionnaire.title": "Kilka pytań o firmę",
  "onboarding.questionnaire.description": "Dzięki nim pokażemy Ci tylko to, co naprawdę Ciebie dotyczy.",

  // — Połączenia (§14)
  "connections.title": "Połączenia",
  "connections.description": "Podłącz pocztę i kalendarz, żeby VeyCue widział terminy, zanim staną się problemem.",
  "connections.connect": "Podłącz",
  "connections.disconnect": "Odłącz",
  "connections.status.active": "Aktywne",
  "connections.status.needs_reauth": "Wymaga ponownej autoryzacji",
  "connections.status.error": "Błąd synchronizacji",
  "connections.lastSync": "Ostatnia synchronizacja: {time}",

  // — Dokumenty
  "documents.title": "Dokumenty",
  "documents.upload": "Dodaj dokument",
  "documents.scan": "Zeskanuj aparatem",
  "documents.status.processing": "Analizujemy…",
  "documents.status.processed": "Przeanalizowany",
  "documents.status.failed": "Nie udało się przeanalizować",
  "documents.empty": "Nie masz jeszcze żadnych dokumentów.",

  // — Ask VeyCue (§21, §40)
  "ask.title": "Zapytaj VeyCue",
  "ask.placeholder": "Co wymaga mojej uwagi w tym tygodniu?",
  "ask.disclaimer": "Odpowiedzi opierają się na dokumentach Twojej firmy. VeyCue nie świadczy usług prawnych, księgowych ani doradztwa podatkowego.",
  "ask.aiNotice": "Odpowiedź wygenerowana przez AI",

  // — Weekly Brief (§25)
  "brief.title": "Twoja firma w tym tygodniu",
  "brief.estimatedTime": "Szacowany czas: {minutes} min",

  // — Błędy (kody z packages/shared)
  "error.validation_failed": "Sprawdź wprowadzone dane.",
  "error.invalid_credentials": "Nieprawidłowy adres e-mail lub hasło.",
  "error.unauthenticated": "Zaloguj się, aby kontynuować.",
  "error.token_expired": "Sesja wygasła. Zaloguj się ponownie.",
  "error.token_reuse_detected": "Ze względów bezpieczeństwa wylogowaliśmy wszystkie sesje. Zaloguj się ponownie.",
  "error.forbidden": "Nie masz uprawnień do tej operacji.",
  "error.insufficient_role": "Ta operacja wymaga wyższych uprawnień.",
  "error.not_found": "Nie znaleziono zasobu.",
  "error.already_exists": "Taki zasób już istnieje.",
  "error.rate_limited": "Zbyt wiele prób. Spróbuj ponownie za chwilę.",
  "error.entitlement_required": "Ta funkcja wymaga aktywnej subskrypcji.",
  "error.ai_budget_exceeded": "Wykorzystałeś miesięczny limit analiz AI w swoim planie.",
  "billing.usageTitle": "Zużycie analiz AI",
  "billing.usedOfBudget": "Wykorzystano {used}% miesięcznego limitu",
  "billing.budgetReached": "Limit analiz w planie {from} został wykorzystany. Plan {to} daje {multiplier}× większy budżet — nowe dokumenty będą analizowane od razu.",
  "billing.trialBudgetReached": "Okres próbny wykorzystał przewidziany limit analiz. Wybierz plan {to}, żeby VeyCue dalej pilnował Twoich terminów.",
  "billing.upgradeCta": "Zmień plan",
  "billing.degradedNotice": "Nowe dokumenty są nadal przyjmowane i rozpoznawane, ale bez pełnej analizy AI.",
  "error.internal_error": "Coś poszło nie tak. Spróbuj ponownie.",
  "error.offline": "Brak połączenia z internetem.",

  // — Strona marketingowa (§47)
  "web.hero.line1": "Twoja firma generuje setki spraw.",
  "web.hero.line2": "Tylko kilka wymaga Ciebie.",
  "web.hero.line3": "VeyCue je znajduje.",
  "web.hero.subtitle": "System oparty na AI, który obserwuje Twoją firmę i pokazuje wyłącznie to, co naprawdę wymaga Twojej uwagi.",
  "web.cta.primary": "Zacznij monitorować firmę",
  "web.cta.secondary": "Zobacz, jak to działa",
  "web.cta.trial": "14 dni bezpłatnie · Bez karty płatniczej",
  "web.trust.ai": "Napędzane przez AI",
  "web.trust.aiDescription": "Inteligentna analiza i priorytetyzacja",
  "web.trust.secure": "Bezpieczne z założenia",
  "web.trust.secureDescription": "Twoje dane są szyfrowane i pod kontrolą",
  "web.trust.trusted": "Zaufanie firm",
  "web.trust.trustedDescription": "Dla firm, które cenią skupienie",
  "web.pillar.focus": "Skupienie",
  "web.pillar.focusDescription": "Mniej szumu, więcej tego, co ważne.",
  "web.pillar.signals": "Sygnały",
  "web.pillar.signalsDescription": "Wykrywamy sygnały w Twojej firmie.",
  "web.pillar.intelligence": "Inteligencja",
  "web.pillar.intelligenceDescription": "AI analizuje, rozumie i priorytetyzuje.",
  "web.pillar.trust": "Zaufanie",
  "web.pillar.trustDescription": "Bezpieczeństwo danych i pełna kontrola.",
  "web.problem.title": "Problem nie polega na braku informacji",
  "web.problem.description": "Umowy przedłużają się automatycznie. Terminy wypowiedzenia mijają. Polisy wygasają. Nikt tego nie pilnuje, bo wszystko jest w innym miejscu.",
  "web.how.title": "Jak to działa",
  "web.how.step1": "Podaj NIP i odpowiedz na kilka pytań.",
  "web.how.step2": "Wrzuć umowy i podłącz pocztę oraz kalendarz.",
  "web.how.step3": "Dostajesz tylko te sygnały, które naprawdę wymagają decyzji.",
  "web.pricing.title": "Cennik",
  "web.faq.title": "Najczęstsze pytania",
  "web.nav.product": "Produkt",
  "web.nav.useCases": "Zastosowania",
  "web.nav.pricing": "Cennik",
  "web.nav.security": "Bezpieczeństwo",
  "web.nav.login": "Zaloguj się",
  // Krótki wariant CTA do nagłówka — pełne „Rozpocznij bezpłatny okres próbny"
  // rozbijało pasek nawigacji na telefonie na kilka wierszy.
  "web.nav.trial": "Wypróbuj",
  "web.footer.privacy": "Prywatność",
  "web.footer.terms": "Regulamin",
  "web.footer.security": "Bezpieczeństwo",
  "web.footer.contact": "Kontakt",
} as const;
